import { useCallback, useState } from 'react';
import { Link, NavLink } from 'react-router';
import { BotaoTema } from './BotaoTema';
import { MenuCelular } from './MenuCelular';
import { LogoMPC } from './LogoMPC';
import { NotificationCenter } from './NotificationCenter';
import { SaldoNoCabecalho } from './MoedaMpc';
import { BotaoLink } from './ui/Botao';
import { useAuth } from '@/lib/auth';
import '@/styles/cabecalho.css';

/**
 * Classificação e artilharia não estão aqui de propósito: os dois são dados de
 * uma edição, e vivem nas abas do próprio campeonato. No topo eles obrigavam a
 * escolher a edição num seletor antes de mostrar qualquer coisa — um passo a
 * mais para chegar onde `/campeonatos` já leva.
 *
 * Rankings entra no topo pelo motivo oposto: abre somando todas as edições, sem
 * pedir escolha nenhuma, e é a página a que quem joga a Hexa Cup volta para ver onde
 * está.
 */
const NAV = [
  { to: '/', label: 'Início', end: true },
  { to: '/campeonatos', label: 'Campeonatos' },
  { to: '/times', label: 'Times' },
  { to: '/rankings', label: 'Ranking' },
  { to: '/regulamento', label: 'Regulamento' },
  { to: '/loja', label: 'Loja' },
] as const;

/**
 * O cabeçalho do site, o mesmo em todas as rotas — a home inclusive.
 *
 * É a barra da home: fundo chapado com o fio amarelo em cima, a taça e o nome
 * à esquerda, os links no meio com o ativo sublinhado em amarelo e a conta à
 * direita. Fica fixo, porque numa classificação ou no painel o menu precisa
 * continuar à mão; a altura mora em `--altura-cabecalho` (`cabecalho.css`),
 * e é dela que a primeira dobra da home desconta o espaço.
 *
 * Abaixo de 1280px os links vão para o `MenuCelular`: com seis links, o saldo,
 * as notificações e o nome de quem está logado, a linha não cabe antes disso.
 */
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const fechar = useCallback(() => setOpen(false), []);
  const { user, loading, isStaff } = useAuth();

  return (
    <header className="hx-cabecalho">
      <div className="hx-cabecalho-linha">
        <Link to="/" className="hx-cabecalho-marca" aria-label="Hexa Cup — página inicial">
          <LogoMPC altura={52} />
          <span>
            HEXA CUP
            <small>PRO CLUBS</small>
          </span>
        </Link>

        <nav className="hx-cabecalho-nav" aria-label="Principal">
          {linksDoMenu(isStaff).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hx-cabecalho-conta">
          <BotaoTema className="hidden sm:inline-flex" />
          {user || loading ? (
            <SessionArea />
          ) : (
            <Link to="/entrar" className="hx-cabecalho-entrar">
              Entrar
            </Link>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="menu-celular"
            className="hx-cabecalho-menu"
          >
            {open ? 'Fechar' : 'Menu'}
          </button>
        </div>
      </div>

      <MenuCelular aberto={open} onFechar={fechar} />
    </header>
  );
}

/**
 * Os links do menu. A central de comando entra no fim para quem é da
 * organização: quem administra a competição não deveria caçar o painel dentro
 * de um menu de avatar.
 */
function linksDoMenu(isStaff: boolean) {
  return [
    ...NAV.map((item) => ({ to: item.to, label: item.label, end: 'end' in item ? item.end : false })),
    ...(isStaff ? [{ to: '/admin', label: 'Painel', end: true }] : []),
  ];
}

/**
 * Canto direito do cabeçalho.
 *
 * Visitante vê "Entrar" e "Criar conta" (no celular, os dois ficam no menu). Capitão logado vê o atalho para o
 * time dele — que é o lugar para onde ele volta toda vez. Quem é da organização
 * ganha também o link do painel, sem precisar decorar a URL.
 */
export function SessionArea() {
  const { user, teams, isStaff, isAdmin, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="h-12 w-36 animate-pulse bg-surface-2" aria-hidden />;

  if (!user) {
    return (
      <>
        {/* Os dois botões do template: contorno claro e o branco, este com o
            limão subindo no hover — o "Inscrever time" do menu da home. */}
        <BotaoLink to="/entrar" variante="contorno" tamanho="md" className="hidden sm:inline-flex">
          Entrar
        </BotaoLink>
        <BotaoLink to="/cadastrar-time" variante="claro" tamanho="md" className="hidden sm:inline-flex">
          Criar conta
        </BotaoLink>
      </>
    );
  }

  const team = teams[0];

  return (
    <div className="flex items-center gap-2">
      <SaldoNoCabecalho />
      <NotificationCenter />

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-2.5 bg-(--vidro) py-2 pr-4 pl-2 text-base font-medium transition-colors hover:bg-brand hover:text-black"
        >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="size-8" />
        ) : (
          <span className="flex size-8 items-center justify-center bg-brand text-xs font-bold text-black">
            {user.displayName.slice(0, 2).toUpperCase()}
          </span>
        )}
        <span className="hidden max-w-28 truncate sm:inline">{user.displayName}</span>
      </button>

      {open && (
        <>
          {/* Camada invisível que fecha o menu ao clicar fora, sem listener global. */}
          <button
            type="button"
            aria-label="Fechar menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div className="mpc-panel absolute right-0 z-50 mt-2 w-56 overflow-hidden py-1 shadow-[var(--shadow-lift)]">
            <p className="truncate border-b border-line px-4 py-2.5 text-xs text-faint">
              {user.email ?? user.displayName}
            </p>

            {/* O nome do time abre o perfil dele, como qualquer visitante vê;
                a edição fica no botão "Editar clube" do próprio perfil. */}
            {team ? (
              <Link
                to={`/times/${team.slug}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-paper"
              >
                {team.name}
              </Link>
            ) : (
              <Link
                to="/meu-time/novo"
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm text-brand-ink transition-colors hover:bg-surface-2"
              >
                Cadastrar meu time
              </Link>
            )}

            {/* Bloco da organização.
                Só aparece para quem tem papel `admin` ou `staff` — e não é só
                a tela que esconde: cada rota de `/api/admin/*` exige o papel no
                servidor. Esconder o link é conveniência; a permissão de verdade
                mora na API, onde ninguém contorna abrindo o endereço na mão. */}
            {isStaff && (
              <>
                <p className="border-t border-line px-4 pt-3 pb-1 text-xs font-medium text-faint">
                  Organização
                </p>
                {[
                  { to: '/admin', label: 'Central de comando' },
                  { to: '/admin/campeonatos', label: 'Campeonatos' },
                  { to: '/admin/tickets', label: 'Tickets' },
                  { to: '/admin/moedas', label: 'Moedas & Loja' },
                  // Só admin: o coletor é infraestrutura da organização, e a
                  // listagem devolve o IP residencial de quem hospeda o agente.
                  ...(isAdmin
                    ? [
                        { to: '/admin/usuarios', label: 'Usuários & Permissões' },
                        { to: '/admin/coletor', label: 'Agentes do coletor' },
                      ]
                    : []),
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-brand-ink"
                  >
                    {item.label}
                  </Link>
                ))}
              </>
            )}

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="block w-full border-t border-line px-4 py-2.5 text-left text-sm text-muted transition-colors hover:bg-surface-2 hover:text-loss"
            >
              Sair
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
